import 'rxjs/add/observable/fromEventPattern';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/switchMapTo';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {Observable} from 'rxjs/Observable';

import {AuthType, ErrorCode, headerNames} from '../constants';
import {facebookApi, FB, LoginStatus} from '../core/facebook-api';
import {SnapAuth} from '../core/snap-auth';
import {LogService} from '../shared/log';
import {SignInControl, SignUpControl, UiService} from '../shared/ui';

export class FacebookComponent {
  private readonly formName = 'alt';

  private readonly status$ = new BehaviorSubject<LoginStatus>({status: ''});

  constructor(
    private readonly fb: FB,
    private readonly snapAuth: SnapAuth,
    private readonly signInControl: SignInControl,
    private readonly signUpControl: SignUpControl,
    private readonly ui: UiService,
    private readonly log: LogService,
    private readonly SCOPE: string,
  ) {
    this.signInControl.hidden = false;
    this.signUpControl.hidden = true;

    this.handleSignUpInteraction = this.handleSignUpInteraction.bind(this);
    this.listenForControlInteraction = this.listenForControlInteraction.bind(this);
    this.listenForStatusChange = this.listenForStatusChange.bind(this);
    this.signIn = this.signIn.bind(this);

    Observable.fromEventPattern(this.listenForStatusChange)
      .subscribe(this.status$);

    Observable.fromEventPattern(this.listenForControlInteraction)
      .switchMapTo(this.status$)
      .switchMap(this.signIntoFacebook({scope: this.SCOPE}))
      .filter(FacebookComponent.isConnected)
      .subscribe(this.signIn);

    // Listen for click on sign up button
    this.signUpControl.registerInteractionHandler('click', this.handleSignUpInteraction);
  }

  private async handleSignUpInteraction() {
    const loginStatus = await facebookApi.loginStatus();
    if (FacebookComponent.isConnected(loginStatus)) {
      await this.signUp(loginStatus);
    }
  }

  private listenForControlInteraction(handler: () => void) {
    this.signInControl.registerInteractionHandler('click', handler);
  }

  private listenForStatusChange(handler: (loginStatus: LoginStatus) => void) {
    this.fb.Event.subscribe('auth.statusChange', handler);
  }

  private navigate(response: Response) {
    const redirectUrl = response.headers.get(headerNames.REDIRECT_URL);
    window.location.href = redirectUrl || '/';
  }

  private setHelptext(message = '', messages?: Map<string, string>) {
    this.ui.forms.setHelptext(new Map([[this.formName, message]]));
    this.ui.controls.setHelptext(messages);
  }

  private async signIn(loginStatus: LoginStatus) {
    try {
      this.ui.masterControl.disabled = true;
      this.ui.masterControl.progress = true;
      this.setHelptext();

      const response = await this.snapAuth.signIn(AuthType.Facebook, loginStatus.authResponse);

      this.navigate(response);

    } catch (ex) {
      this.ui.masterControl.disabled = false;
      this.ui.masterControl.progress = false;

      if (this.snapAuth.isAuthException(ex)) {
        this.setHelptext(ex.message, ex.controls);

        if (ex.code === ErrorCode.AccountDoesNotExist) {
          this.signUpControl.hidden = false;
          this.signUpControl.focus();
        }

        if (ex.code === ErrorCode.RequirePreviousAuth && ex.response != null) {
          const pendingAuth = ex.response.headers.get(headerNames.PENDING_AUTH);
          this.snapAuth.setPendingAuth(pendingAuth || '');
        }

      } else {
        this.setHelptext(`Can't sign in`);
        const message = typeof ex === 'string' ? ex : ex.message;
        this.log.error(message);
        this.log.sendError(message);
      }
    }
  }

  private async signUp(loginStatus: LoginStatus) {
    try {
      this.ui.masterControl.disabled = true;
      this.ui.masterControl.progress = true;
      this.setHelptext();

      const response = await this.snapAuth.signUp(AuthType.Facebook, loginStatus.authResponse);

      this.navigate(response);

    } catch (ex) {
      this.ui.masterControl.disabled = false;
      this.ui.masterControl.progress = false;

      if (this.snapAuth.isAuthException(ex)) {
        this.setHelptext(ex.message, ex.controls);

      } else {
        this.setHelptext(`Can't sign up`);
        const message = typeof ex === 'string' ? ex : ex.message;
        this.log.error(message);
        this.log.sendError(message);
      }
    }
  }

  private signIntoFacebook(options = {}) {
    return (loginStatus: LoginStatus) => {
      return FacebookComponent.isConnected(loginStatus) ?
        Promise.resolve(loginStatus) :
        new Promise<LoginStatus>(resolve => this.fb.login(resolve, options));
    };
  }

  private static isConnected({status}: LoginStatus) {
    return status === 'connected';
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/facebook.component.ts